import { useCallback, useEffect, useMemo } from 'react';

import { useEnableLoyaltyServiceRewards } from 'features/reward/hooks/use-enable-loyalty-service-rewards';
import { useServiceRewards } from 'features/reward/state/service-rewards-context';
import { useStoreContext } from 'features/store';

import { LoyaltyServiceMode } from 'generated/graphql-gateway';
import { useCmsConfigRewardsQuery, useLoyaltyRewardsQuery } from 'generated/sanity-graphql';
import { useLoyaltyEngineRewards } from 'hooks/use-loyalty-engine-rewards';
import { useAuthContext } from 'state/auth';
import { withSimpleCache } from 'state/graphql/with-simple-cache';
import { useLoyaltyUser, useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { ISanityRewardsMap, LoyaltyReward } from 'state/loyalty/types';
import { getServiceModeFromLS } from 'state/service-mode';

const useCachedLoyaltyRewardsQuery = withSimpleCache(useLoyaltyRewardsQuery);

const getServiceModeForLoyalty = () => {
  const serviceMode = getServiceModeFromLS();
  return serviceMode
    ? LoyaltyServiceMode[serviceMode as keyof typeof LoyaltyServiceMode]
    : undefined;
};

export const useLoyaltyRewardsList = () => {
  const { store } = useStoreContext();
  const { isAuthenticated } = useAuthContext();
  const { loyaltyUserId } = useLoyaltyUserId();
  const { loyaltyUser } = useLoyaltyUser();
  const { enabled: serviceRewardsEnabled } = useEnableLoyaltyServiceRewards();
  const serviceRewards = useServiceRewards();
  const refreshServiceRewards = serviceRewards.refreshRewards;

  const {
    engineRewardsLoading,
    refetchEngineRewards,
    engineRewardIds,
    engineRewardsMap,
    personalizedRewardIds,
    personalizedRewardMap,
  } = useLoyaltyEngineRewards({ skip: serviceRewardsEnabled });

  // fetch all config rewards by ID
  const { data: configRewardsData, loading: configRewardsLoading } = useCmsConfigRewardsQuery({
    variables: {
      ids: personalizedRewardIds,
    },
    skip: serviceRewardsEnabled || !isAuthenticated || !personalizedRewardIds?.length,
  });

  const {
    data,
    loading: sanityRewardsLoading,
    refetch: refetchCmsRewards,
  } = useCachedLoyaltyRewardsQuery({
    skip: serviceRewardsEnabled || !engineRewardIds?.length,
  });

  const refetchRewards = useCallback(
    async (loyaltyId: string) => {
      if (serviceRewardsEnabled) {
        await refreshServiceRewards();
        return;
      }
      await refetchEngineRewards({
        loyaltyId: loyaltyId || loyaltyUserId || '',
        where: {
          ignorePointBalance: true,
          serviceMode: getServiceModeForLoyalty() || undefined,
          storeId: store?.number,
        },
      });
      await refetchCmsRewards();
    },
    [
      loyaltyUserId,
      refetchCmsRewards,
      refetchEngineRewards,
      serviceRewardsEnabled,
      refreshServiceRewards,
      store?.number,
    ]
  );

  const rewardsByEngineId: LoyaltyReward[] | null = useMemo(() => {
    if (data?.allRewards) {
      const validRewards = data.allRewards.filter(
        reward => engineRewardsMap[reward?.loyaltyEngineId || '']
      );

      return [...validRewards, ...(configRewardsData?.allConfigRewards ?? [])];
    }
    return null;
  }, [data, engineRewardsMap, configRewardsData]);

  const sanityRewardsMap = useMemo(() => {
    if (!rewardsByEngineId) {
      return null;
    }

    const rewardsMap = rewardsByEngineId.reduce((acc: ISanityRewardsMap, reward) => {
      acc[reward._id] = reward;
      return acc;
    }, {});

    return Object.keys(personalizedRewardMap).reduce((acc: ISanityRewardsMap, id) => {
      const { sanityId } = personalizedRewardMap[id];
      if (sanityId) {
        rewardsMap[id] = acc[sanityId];
      }
      return rewardsMap;
    }, rewardsMap);
  }, [personalizedRewardMap, rewardsByEngineId]);

  const rewardsLoading = engineRewardsLoading || sanityRewardsLoading || configRewardsLoading;

  // Refetch when user points change
  useEffect(() => {
    if (!serviceRewardsEnabled && loyaltyUser && loyaltyUser.points) {
      refetchEngineRewards();
    }
  }, [loyaltyUser, refetchEngineRewards, serviceRewardsEnabled]);

  if (serviceRewardsEnabled) {
    return {
      allRewards: serviceRewards.allRewards,
      rewards: serviceRewards.allRewards,
      loading: serviceRewards.loading,
      engineRewardsMap: serviceRewards.engineRewardsMap,
      sanityRewardsMap: serviceRewards.sanityRewardsMap,
      refetchRewards,
    };
  }

  return {
    allRewards: data?.allRewards,
    rewards: rewardsByEngineId || null,
    loading: rewardsLoading,
    engineRewardsMap,
    sanityRewardsMap,
    refetchRewards,
  };
};
